import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import { useTopology, useSnapshot, useRecent } from "@/store/site";
import { PageShell } from "./PageShell";
import { Card, CardHeader, CardTitle, GranBadge } from "@/components/ui/Card";
import { KpiStrip } from "@/components/ui/KpiStrip";
import { BessTile } from "@/components/BessTile";
import { carryTo } from "@/lib/carryParams";
import { signedKW, bessDirection } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/cn";
import { baseOption, chartTokens, hexA, unitColor } from "@/lib/chartTheme";
import { prepareLineTuples } from "@/lib/chartGaps";
import { useThemeVersion } from "@/hooks/useThemeVersion";
import { ArrowRight, Battery, Cpu, Layers, Thermometer, Zap, ZapOff } from "lucide-react";
import { BessSocPowerChart, bessChartSummary } from "@/components/charts/BessSocPowerChart";
import { BessEnergyBrowser } from "@/components/charts/BessEnergyBrowser";
import { AvailabilityCard } from "@/components/charts/AvailabilityRibbon";
import { SoHPanel } from "@/components/SoHPanel";
import { BessFleetVerdict } from "@/components/BessFleetVerdict";
import type { SiteSnapshot, BessUnitSnapshot } from "@/data/types";

export default function BessPage() {
  const t = useT();
  const topology = useTopology();
  const snap = useSnapshot();
  const recent = useRecent();
  const location = useLocation();
  const agg = snap.bess_aggregate;
  const totalCharge = snap.energy_today.bess_charge_kWh;
  const totalDischarge = snap.energy_today.bess_discharge_kWh;
  const sum = bessChartSummary(recent);
  // Aggregate-power tone follows the live direction (BESS-08) — a fleet
  // discharging at −180 kW must not wear the charge colour.
  const aggDir = bessDirection(agg.active_W);
  // Fleet chart SOC mode (BESS-06): aggregate average vs one line per unit.
  // Local, deliberately non-persisted view state.
  const [perUnit, setPerUnit] = useState(false);

  // Fleet chart → Historical (LINK-08): same aggregate series via the
  // curated keys Historical offers. The chart shows the last 60 min, so
  // when no window is being carried, default to the matching live-1h
  // preset instead of Historical's 24h default.
  const sp = new URLSearchParams(location.search);
  const hasCarriedWindow = Boolean((sp.get("start") && sp.get("end")) || sp.get("preset"));
  const fleetHistHref = carryTo("/historical", {
    keys: "battery/aggregation/power,battery/aggregation/SOC",
    ...(hasCarriedWindow ? {} : { preset: "live-1h" }),
  });

  // Worst-first STABLE sort of the fleet tiles (F18): offline < faulted <
  // ok, then lowest-SOC among the ok units, with the topology index as the
  // final tie-break so tiles don't dance on 1 Hz SOC jitter.
  const fleet = useMemo(() => {
    const idx = new Map(topology.bess.map((b, i) => [b.id, i]));
    const rank = (u: BessUnitSnapshot) => (!u.online ? 0 : u.fault_count > 0 ? 1 : 2); // 0=offline worst, 1=faulted, 2=ok
    return [...snap.bess_per_unit].sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      if (rank(a) === 2) { const s = a.soc_pct - b.soc_pct; if (s !== 0) return s; } // ok units: lowest SOC first
      return (idx.get(a.id) ?? 0) - (idx.get(b.id) ?? 0); // STABLE final tie-break = topology index (prevents dancing tiles on 1Hz SOC jitter)
    });
  }, [snap.bess_per_unit, topology.bess]);

  return (
    <PageShell title={t("analyze.bess.title")} caption={t("analyze.bess.caption", { n: topology.bess.length, cap: agg.total_capacity_kWh.toFixed(0), proto: topology.bess[0]?.protocol.toUpperCase() ?? "" })}>
      {/* N06 — verdict-first: the plain-language fleet verdict leads the page,
          above the 6 numeric KPIs and the per-unit tiles, matching the
          answer-first ordering on Overview / Market-price / Revenue. The
          verdict complements (not restates) the "Online x/n" KPI: it names
          the worst unit + faults and drills to it. */}
      <BessFleetVerdict units={fleet} total={topology.bess.length} />
      <KpiStrip>
        <Mini
          icon={Layers}
          label={t("analyze.bess.kpiAggregate")}
          value={`${signedKW(agg.active_W)} kW`}
          sub={aggDir === "charge" ? t("status.charging") : aggDir === "discharge" ? t("status.discharging") : t("status.idle")}
          tone={aggDir === "idle" ? "muted" : aggDir}
        />
        <Mini icon={Battery} label={t("analyze.bess.kpiAvgSoc")} value={`${agg.soc_avg_pct.toFixed(0)}%`} sub={`${agg.soc_min_pct.toFixed(0)} → ${agg.soc_max_pct.toFixed(0)}%`} />
        <Mini icon={Cpu} label={t("analyze.bess.kpiOnline")} value={`${agg.online_count}/${topology.bess.length}`} tone={agg.fault_count > 0 ? "warn" : "ok"} />
        <Mini icon={Zap} label={t("analyze.bess.kpiTodayCharge")} value={`${totalCharge.toFixed(0)} kWh`} tone="charge" />
        <Mini icon={ZapOff} label={t("analyze.bess.kpiTodayDischarge")} value={`${totalDischarge.toFixed(0)} kWh`} tone="discharge" />
        <Mini icon={Thermometer} label={t("analyze.bess.kpiAvgTemp")} value={`${agg.avg_temperature_C.toFixed(1)} °C`} />
      </KpiStrip>

      <Card>
        <CardHeader>
          <CardTitle hint={<GranBadge live label={t("analyze.bess.latestBadge")} />}>{t("analyze.bess.perUnit")}</CardTitle>
          <span className="text-[10px] text-text-muted">{t("analyze.bess.perUnitSub")}</span>
        </CardHeader>
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-4">
          {fleet.map((u) => {
            const cap = topology.bess.find((b) => b.id === u.id)?.capacity_kWh ?? 0;
            return <BessTile key={u.id} unit={u} capacity_kWh={cap} />;
          })}
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle hint={<GranBadge live label={t("analyze.bess.last60")} />}>{t("analyze.bess.fleetTitle")}</CardTitle>
          <div className="flex flex-wrap items-center gap-3 text-[10px] text-text-muted">
            <span>{t("analyze.bess.statPowerRange")} <strong className="num text-text">{sum.p_min_kW.toFixed(1)} → {sum.p_max_kW.toFixed(1)} kW</strong></span>
            <span>{t("analyze.bess.statAvg")} <strong className="num text-text">{sum.p_avg_kW.toFixed(1)} kW</strong></span>
            <span>SOC <strong className="num text-text">{sum.soc_min.toFixed(0)} → {sum.soc_max.toFixed(0)}%</strong></span>
            <div
              className="flex items-center rounded-md border border-edge bg-surface p-0.5"
              role="group"
              aria-label={t("analyze.bess.socModeAria")}
            >
              <button
                type="button"
                onClick={() => setPerUnit(false)}
                title={t("analyze.bess.aggregateTitle")}
                className={cn("rounded px-2 py-0.5 text-[10px] font-medium", !perUnit ? "bg-accent/15 text-accent" : "text-text-muted hover:text-text")}
              >
                {t("analyze.bess.aggregateBtn")}
              </button>
              <button
                type="button"
                onClick={() => setPerUnit(true)}
                title={t("analyze.bess.perUnitTitle")}
                className={cn("rounded px-2 py-0.5 text-[10px] font-medium", perUnit ? "bg-accent/15 text-accent" : "text-text-muted hover:text-text")}
              >
                {t("analyze.bess.perUnitBtn")}
              </button>
            </div>
            <Link to={fleetHistHref} className="flex items-center gap-1 text-[11px] text-accent hover:underline">
              {t("analyze.bess.openHistorical")} <ArrowRight size={11} />
            </Link>
          </div>
        </CardHeader>
        <div className="p-3">
          {perUnit
            ? <BessPerUnitSocChart snapshots={recent} height={240} />
            : <BessSocPowerChart snapshots={recent} height={240} />}
        </div>
      </Card>

      <BessEnergyBrowser />

      {/* Per-unit + grid-meter availability ribbons (gaps are clickable
          forensic links) — the outage history the fleet page was missing. */}
      <AvailabilityCard />

      <SoHPanel snapshot={snap} bess={topology.bess} />
    </PageShell>
  );
}

/**
 * Per-unit SOC overlay for the fleet card (BESS-06). The aggregate chart
 * averages SOC across units, which hides exactly the failure mode that
 * matters — one unit's SOC sagging or oscillating while the fleet trace
 * looks smooth (how the real BESS#3 debounce incident surfaced). This
 * variant keeps the summed active-power series for context and swaps the
 * averaged SOC line for one line per unit.
 *
 * Unit hues come from the shared `unitColor(idx)` identity palette (read
 * live INSIDE the memo; theme-flip safe via useThemeVersion), so BESS-1
 * wears the same hue on every per-unit chart in the app. The ECharts
 * legend maps unit names to their hues. Charge — the 8th hue in the cycle
 * — only recurs at 8+ units; the summed-power area wears it here, which
 * is fine for the fleets we run (4 units).
 */
function BessPerUnitSocChart({
  snapshots,
  height = 240,
}: {
  snapshots: SiteSnapshot[];
  height?: number;
}) {
  const themeVersion = useThemeVersion();
  const option = useMemo(() => {
    const base = baseOption();
    const t = chartTokens();

    const power: [number, number][] = [];
    const units = new Map<string, { name: string; soc: [number, number][] }>();
    for (const s of snapshots) {
      if (!s.bess_per_unit.length) continue;
      power.push([s.ts, s.bess_per_unit.reduce((a, x) => a + x.active_W, 0) / 1000]);
      for (const u of s.bess_per_unit) {
        let entry = units.get(u.id);
        if (!entry) {
          entry = { name: u.name, soc: [] };
          units.set(u.id, entry);
        }
        entry.soc.push([s.ts, u.soc_pct]);
      }
    }
    const unitList = [...units.values()];

    return {
      ...base,
      grid: { ...base.grid, top: 32, left: 56, right: 56, bottom: 44, containLabel: true },
      xAxis: { ...(base.xAxis as object), type: "time" },
      yAxis: [
        {
          name: "kW",
          nameLocation: "end",
          nameTextStyle: { color: t.textMuted, fontSize: 10, padding: [0, 0, 0, -24] },
          type: "value",
          scale: true,
          axisLabel: { color: t.textMuted, fontSize: 11, formatter: (v: number) => `${v.toFixed(0)}` },
          splitLine: { lineStyle: { color: t.grid, type: "dashed" } },
        },
        {
          name: "SOC %",
          nameLocation: "end",
          nameTextStyle: { color: t.textMuted, fontSize: 10 },
          type: "value",
          position: "right",
          min: 0,
          max: 100,
          axisLabel: { color: t.textMuted, fontSize: 11, formatter: (v: number) => `${v.toFixed(0)}%` },
          splitLine: { show: false },
        },
      ],
      legend: { ...(base.legend as object), data: ["Active power", ...unitList.map((u) => u.name)] },
      dataZoom: [
        { type: "inside", xAxisIndex: 0, filterMode: "none", zoomOnMouseWheel: true, moveOnMouseWheel: false, throttle: 50 },
        {
          type: "slider",
          xAxisIndex: 0,
          filterMode: "none",
          bottom: 4,
          height: 18,
          showDetail: false,
          borderColor: t.tooltipBorder,
          backgroundColor: hexA(t.textFaint, 0.06),
          fillerColor: hexA(t.accent, 0.12),
          handleStyle: { color: t.accent, borderColor: t.accent },
          moveHandleStyle: { color: t.accent },
          dataBackground: {
            lineStyle: { color: hexA(t.charge, 0.5), width: 1 },
            areaStyle: { color: hexA(t.charge, 0.10) },
          },
          selectedDataBackground: {
            lineStyle: { color: t.accent, width: 1 },
            areaStyle: { color: hexA(t.accent, 0.18) },
          },
        },
      ],
      series: [
        {
          name: "Active power",
          type: "line",
          showSymbol: false,
          smooth: 0.28,
          connectNulls: false,
          data: prepareLineTuples(power),
          lineStyle: { color: t.charge, width: 2.4 },
          areaStyle: {
            opacity: 1,
            color: {
              type: "linear", x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0,    color: hexA(t.charge, 0.32) },
                { offset: 0.45, color: hexA(t.charge, 0.14) },
                { offset: 1,    color: hexA(t.charge, 0)    },
              ],
            },
          },
          z: 2,
        },
        ...unitList.map((u, i) => ({
          name: u.name,
          type: "line",
          showSymbol: false,
          smooth: 0.25,
          connectNulls: false,
          yAxisIndex: 1,
          data: prepareLineTuples(u.soc),
          lineStyle: { color: unitColor(i), width: 1.8 },
          itemStyle: { color: unitColor(i) },
          z: 3,
        })),
      ],
      tooltip: {
        ...(base.tooltip as object),
        valueFormatter: (v: number | string | null) => {
          if (v == null) return "—";
          const n = typeof v === "number" ? v : Number(v);
          return Number.isFinite(n) ? n.toFixed(2) : "—";
        },
      },
    } as object;
  }, [snapshots, themeVersion]);
  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate />;
}

type MiniTone = "muted" | "charge" | "discharge" | "ok" | "warn";
function Mini({
  icon: Icon, label, value, sub, tone = "muted",
}: { icon: any; label: string; value: string; sub?: string; tone?: MiniTone }) {
  const cls: Record<MiniTone, string> = {
    muted: "text-text",
    charge: "text-charge",
    discharge: "text-discharge",
    ok: "text-ok",
    warn: "text-warn",
  };
  const c = cls[tone];
  return (
    <div className="card flex flex-col gap-1.5 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-faint font-semibold">
        <Icon size={12} /> {label}
      </div>
      <div className={`text-lg font-bold num ${c}`}>{value}</div>
      {sub && <div className="text-[10px] text-text-muted num">{sub}</div>}
    </div>
  );
}

